import React, { useState, useEffect } from 'react';
import { Camera, CircleAlert, RotateCcw, Settings } from 'lucide-react';
import { Button, DialogBody, DialogFooter } from '../ui';

interface BusinessCardScannerProps {
  /** URL of the captured/uploaded business card image */
  imageUrl: string;
  /** Whether AI extraction is currently in progress */
  isProcessing: boolean;
  /** Error message if extraction failed */
  error?: string | null;
  /** Error type for showing appropriate actions */
  errorType?: 'auth' | 'network' | 'api' | 'unknown';
  /** Callback when user wants to retry extraction */
  onRetry?: () => void;
  /** Callback when user wants to try with a different image */
  onTryAnother?: () => void;
  /** Current retry count */
  retryCount?: number;
  /** Maximum retries allowed */
  maxRetries?: number;
}

const STATUS_MESSAGES = ['Analyzing image', 'Extracting contact details', 'Identifying organization', 'Almost done'];

/**
 * BusinessCardScanner — the card image with a calm progress line while AI
 * extraction runs, and the retry / try-another actions when it fails.
 * Renders a dialog body (and footer on error) for the import sheet.
 */
export const BusinessCardScanner: React.FC<BusinessCardScannerProps> = ({
  imageUrl,
  isProcessing,
  error,
  errorType = 'unknown',
  onRetry,
  onTryAnother,
  retryCount = 0,
  maxRetries = 3,
}) => {
  const [statusIndex, setStatusIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  // Cycle through status messages during processing
  useEffect(() => {
    if (!isProcessing) {
      setStatusIndex(0);
      return;
    }

    const timers = STATUS_MESSAGES.map((_, index) => {
      if (index === 0) return null;
      return setTimeout(() => setStatusIndex(index), index * 2500);
    });

    return () => {
      timers.forEach((timer) => timer && clearTimeout(timer));
    };
  }, [isProcessing]);

  // Progress that eases towards ~92% while waiting; the sheet moves on when extraction finishes
  useEffect(() => {
    if (!isProcessing) {
      setProgress(0);
      return;
    }
    setProgress(6);
    const interval = window.setInterval(() => {
      setProgress((p) => p + (92 - p) * 0.06);
    }, 250);
    return () => window.clearInterval(interval);
  }, [isProcessing]);

  const canRetry = retryCount < maxRetries && errorType !== 'auth';
  const failed = Boolean(error) && !isProcessing;

  return (
    <>
      <DialogBody className="pb-6 pt-3 sm:pb-5">
        <div style={failed ? undefined : { paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div
            className="relative mx-auto w-full max-w-sm overflow-hidden rounded-2xl bg-neutral-100 ring-1 ring-inset ring-black/[0.06]"
            style={{ aspectRatio: '1.586' }}
          >
            <img
              src={imageUrl}
              alt="Business card"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity ${failed ? 'opacity-50 grayscale' : ''}`}
            />
            {isProcessing && (
              <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
                <div className="animate-scan-line absolute left-0 right-0 h-px bg-primary-600/60" />
              </div>
            )}
          </div>

          {isProcessing && (
            <div className="mx-auto mt-5 w-full max-w-sm" role="status" aria-live="polite">
              <div
                className="h-[3px] w-full overflow-hidden rounded-full bg-neutral-200/80"
                role="progressbar"
                aria-label="Extracting"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progress)}
              >
                <div
                  className="h-full rounded-full bg-primary-600 transition-[width] duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-2.5 flex items-center justify-between gap-3 text-[13px]">
                <span className="text-ink">{STATUS_MESSAGES[statusIndex]}…</span>
                <span className="text-[12px] text-neutral-400">A few seconds</span>
              </div>
            </div>
          )}

          {failed && (
            <div className="mx-auto mt-4 w-full max-w-sm space-y-2" role="alert">
              <p className="flex items-start gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
                <CircleAlert className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                <span className="min-w-0 break-words">{error}</span>
              </p>
              {errorType === 'auth' && (
                <p className="px-1 text-[12px] text-neutral-400">Check the AI provider in settings.</p>
              )}
            </div>
          )}
        </div>
      </DialogBody>

      {failed && (
        <DialogFooter>
          {onTryAnother && (
            <Button variant="secondary" onClick={onTryAnother} className="h-10 flex-1 sm:h-9 sm:flex-none">
              <Camera className="h-4 w-4" strokeWidth={1.75} />
              Try another
            </Button>
          )}
          {canRetry && onRetry && (
            <Button
              variant="primary"
              onClick={onRetry}
              className="h-10 flex-1 sm:h-9 sm:flex-none"
              title={`${maxRetries - retryCount} ${maxRetries - retryCount === 1 ? 'retry' : 'retries'} left`}
            >
              <RotateCcw className="h-4 w-4" strokeWidth={1.75} />
              Retry
            </Button>
          )}
          {errorType === 'auth' && (
            <Button
              variant="primary"
              onClick={() => window.open('/settings', '_blank')}
              className="h-10 flex-1 sm:h-9 sm:flex-none"
            >
              <Settings className="h-4 w-4" strokeWidth={1.75} />
              AI settings
            </Button>
          )}
        </DialogFooter>
      )}
    </>
  );
};
